import { Link } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Sparkline } from "@/components/charts/Sparkline";
import { PageHeaderBar } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { BelowTargetCell } from "@/components/profitability/BelowTargetCell";
import { EmptyBook, isEmptyBook, YourBook } from "@/components/profitability/BookNotice";
import { ClientPartnerCell } from "@/components/profitability/ClientPartnerCell";
import {
  ALL_CONTRACT_TYPES,
  ContractTypeSelect,
} from "@/components/profitability/ContractTypeSelect";
import { clientPartnerFilter, UNASSIGNED } from "@/components/profitability/clientPartner";
import {
  ActiveFilterBar,
  FilterButton,
  FilterDialog,
} from "@/components/profitability/FilterDialog";
import type { FilterDef } from "@/components/profitability/filters";
import { MarginBadge } from "@/components/profitability/MarginBadge";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { KpiTile } from "@/components/shared/kpi-tile";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { StatusChip } from "@/components/shared/status-chip";
import { Card } from "@/components/ui/card";
import { useAccounts } from "@/hooks/use-accounts";
import { useBookCount } from "@/hooks/use-book-count";
import { useContractTypeParam } from "@/hooks/use-contract-type-param";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { useFilterParams } from "@/hooks/use-filter-params";
import { useRangeParam } from "@/hooks/use-range-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { money, percent } from "@/lib/format";
import type { AccountRow, AccountsTotals } from "@/lib/profitability-api";

/**
 * The filters this grid offers. Module-level so the array identity is stable:
 * the filter hook and the two memos below all take it as a dependency, and a
 * fresh array every render would rebuild the options on every keystroke.
 */
const ACCOUNT_FILTERS: FilterDef<AccountRow>[] = [clientPartnerFilter<AccountRow>()];
const ACCOUNT_FILTER_KEYS = ACCOUNT_FILTERS.map((def) => def.key);

/**
 * This filter is applied by the SERVER, not over the rows in memory.
 *
 * It briefly was client-side, and on a grid that pages at 50 of 116 that made
 * it a lie: the options were the partners on the current page, ticking a name
 * narrowed that page alone, and paging forward filtered page two by the same
 * name rather than showing page two of that partner's accounts. "Show me my
 * accounts" has to mean all of them.
 */
const FILTER_SCOPE =
  "Filters every account, not just this page -- the server applies it before paging.";

// A stable empty array for the memos below, for the same reason the filter
// array is module-level: `data?.rows ?? []` is a new array every render.
const EMPTY_ROWS: AccountRow[] = [];

/**
 * The summary strip, from the engine's whole-result-set totals. Rendered only
 * when the engine sends them: an older server has no `totals`, and four tiles
 * of zeros would read as a real answer. Nothing here is summed from rows.
 */
function AccountsStrip({ totals }: { totals: AccountsTotals }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <KpiTile
        label="Accounts"
        value={totals.accounts.toLocaleString()}
        hint={totals.margin_pct === null ? undefined : `${percent(totals.margin_pct)} margin`}
      />
      <KpiTile
        label="Shortfall"
        value={money(totals.shortfall)}
        tone={totals.shortfall > 0 ? "bad" : "default"}
        hint="dollars below target across the book"
      />
      <KpiTile
        label="Internal share"
        value={percent(totals.internal_share_pct)}
        hint="of booked hours, non-billable"
      />
      <KpiTile
        label="Overdue invoices"
        value={totals.overdue_count.toLocaleString()}
        tone={totals.overdue_count > 0 ? "bad" : "default"}
        hint={`${money(totals.overdue_value)} outstanding`}
      />
    </div>
  );
}

// The server lists periods newest-first ("August 2026", ..., "September
// 2025"); sort chronologically here rather than trust that order so the
// label always reads oldest -> newest regardless of how the API emits them.
export function AccountsPage() {
  // Biggest dollar shortfall against the benchmark first, not the worst
  // percentage -- a rounding-error margin on a tiny account shouldn't outrank
  // a real six-figure gap. This mirrors the server's own default sort, so the
  // first request asks for exactly what the server would return anyway; the
  // user can still re-sort from here.
  // The range drives every figure on this screen, and lives in the URL so the
  // drill-in below can hand the account page the same months.
  const [range, setRange] = useRangeParam();
  const [contractType, setContractType] = useContractTypeParam();
  // Mirrors useContractTypeParam's own rule: "all" is the default, so it stays
  // out of the URL rather than riding along as a redundant param.
  const contractTypeParam = contractType === ALL_CONTRACT_TYPES ? undefined : contractType;
  const grid = useDataGrid({ defaultSort: "gap_vs_benchmark", defaultSortDir: "desc" });
  const [filterValues, setFilterValues] = useFilterParams(ACCOUNT_FILTER_KEYS);
  const { data, isLoading, isError, error, refetch } = useAccounts(
    grid.query,
    grid.pageRequest,
    range,
    contractType,
    filterValues.client_partner,
  );

  // The selections live in the URL for the reason every other control here
  // does: people screenshot this screen and paste the link beside it, and a
  // filtered table whose filter lives in component state reproduces as an
  // unfiltered one.
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The server already applied the filter, so these rows need no second pass.
  const rows = data?.rows ?? EMPTY_ROWS;
  // Options come from the API's full partner list, never from the visible page
  // -- a page-derived list cannot offer a partner who has no account on it.
  const filterOptions = useMemo(
    () => ({
      // No counts: the server applies this filter, so the page cannot know how
      // many accounts sit behind a name without asking for each one. An option
      // with no count renders without one -- see FilterOption.count.
      client_partner: [
        ...(data?.client_partners ?? []).map((value) => ({ value })),
        // The service's partner list holds only partners. Accounts nobody has
        // booked against are 15 of 116 and are a real thing to ask for, so the
        // bucket is offered here and the endpoint takes it by name.
        { value: UNASSIGNED },
      ],
    }),
    [data?.client_partners],
  );

  // The benchmark is a server setting, not a constant in the UI, so the help
  // quotes whatever the API reported rather than hardcoding 55% and drifting
  // silently the day someone changes it.
  const benchmarkPct = data?.benchmark_pct ?? null;
  const belowTargetHelp = [
    COLUMN_HELP.belowTarget,
    COLUMN_HELP.belowTargetPoints,
    benchmarkPct === null ? null : `The benchmark is ${benchmarkPct}% margin.`,
  ]
    .filter(Boolean)
    .join(" ");

  const contractTypes = data?.contract_types;
  const rangeInfo = data?.range;
  // Published before the error branch, so a failed load still shows the title
  // and the filters that caused it rather than an unlabelled error.
  // No subtitle count: the pagination line below already states how many
  // accounts there are, and states it against what is on screen.
  usePageHeader(
    () => (
      <PageHeaderBar
        title="Accounts"
        filters={
          <>
            <SearchInput
              value={grid.searchInput}
              onValueChange={grid.setSearch}
              placeholder="Search accounts..."
              className="w-[220px]"
            />
            <FilterButton values={filterValues} onOpen={() => setFiltersOpen(true)} />
            <ContractTypeSelect
              types={contractTypes}
              value={contractType}
              onChange={setContractType}
            />
          </>
        }
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={setRange}
      />
    ),
    [
      grid.searchInput,
      grid.setSearch,
      filterValues,
      contractTypes,
      contractType,
      setContractType,
      rangeInfo,
      range,
      setRange,
    ],
  );

  // The "Your book" count: Aggregate only, before search and filters.
  const bookCount = useBookCount("accounts", range, data?.access !== undefined);

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (isEmptyBook(data?.access)) {
    return (
      <div className="w-full space-y-4">
        <EmptyBook access={data.access} />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <YourBook access={data?.access} count={bookCount.data} noun={["account", "accounts"]} />
      <FilterDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        defs={ACCOUNT_FILTERS}
        options={filterOptions}
        values={filterValues}
        onApply={setFilterValues}
        scopeNote={FILTER_SCOPE}
      />

      <ActiveFilterBar
        defs={ACCOUNT_FILTERS}
        values={filterValues}
        shown={rows.length}
        total={data?.total ?? rows.length}
        // The server filtered, so there is no surviving-of-loaded ratio to
        // quote: it is simply how many accounts matched, across every page.
        countLabel={`${(data?.total ?? rows.length).toLocaleString()} account${
          (data?.total ?? rows.length) === 1 ? "" : "s"
        } match`}
        scopeNote={FILTER_SCOPE}
        onEdit={() => setFiltersOpen(true)}
        onClear={() => setFilterValues({})}
      />

      {data?.totals ? <AccountsStrip totals={data.totals} /> : null}

      <Card flush className="w-full">
        <DataTable
          flush
          dense
          loading={isLoading}
          empty={
            <EmptyState
              icon={Building2}
              title="No accounts match"
              description="Try a different search term."
            />
          }
          sort={grid.sort}
          sortDir={grid.sortDir}
          onSort={grid.toggleSort}
          headers={[
            { label: "Account", sortKey: "account_name", help: COLUMN_HELP.account },
            // Sorts on client_partner_name, the FLAT field, never on
            // `client_partner` itself: the service sorts in memory, and two
            // objects have no ordering, so ordering on the object raises and
            // empties the grid.
            {
              label: "Client Partner",
              sortKey: "client_partner_name",
              help: COLUMN_HELP.clientPartner,
            },
            {
              label: "Margin",
              sortKey: "margin_pct",
              className: "text-right",
              help: COLUMN_HELP.margin,
            },
            // No sortKey: a trend has no single value to order rows by, and a
            // header that looks sortable but does nothing is worse than a plain one.
            { label: "Trend", help: `${COLUMN_HELP.trend} ${COLUMN_HELP.trendDrillIn}` },
            {
              label: "Below target",
              sortKey: "gap_vs_benchmark",
              className: "text-right",
              help: belowTargetHelp,
            },
            {
              label: "Revenue",
              sortKey: "revenue",
              className: "text-right",
              help: COLUMN_HELP.revenue,
            },
            { label: "Cost", sortKey: "cost", className: "text-right", help: COLUMN_HELP.cost },
            {
              label: "Internal share",
              sortKey: "internal_share_pct",
              className: "text-right",
              help: COLUMN_HELP.internalShare,
            },
            {
              label: "Overdue",
              sortKey: "overdue_value",
              className: "text-right",
              help: COLUMN_HELP.overdue,
            },
          ]}
        >
          {rows.map((row) => (
            <tr key={row.account_id} className={ROW_CLASS}>
              <td className="px-4 py-2.5 font-medium">
                <Link
                  to="/dashboard/accounts/$accountId"
                  params={{ accountId: row.account_id }}
                  search={{ range, contract_type: contractTypeParam }}
                  className="hover:underline"
                >
                  {row.account_name ?? row.account_id}
                </Link>
              </td>
              <td className="max-w-48 px-4 py-2.5">
                <ClientPartnerCell owner={row.client_partner} />
              </td>
              <td className="px-4 py-2.5 text-right">
                <MarginBadge value={row.margin_pct} benchmark={data?.benchmark_pct ?? null} />
              </td>
              <td className="px-4 py-2.5">
                {/* The sparkline itself is NOT a drill-in and should not be one:
                  it is 72px wide and carries up to twelve months, so a month is
                  a six-pixel target sitting inside a row that already
                  navigates. The whole cell is the affordance instead, and it
                  leads to the account's Trend tab -- the full-size chart and
                  the period table, where a month is a real target and opens
                  that month's projects. */}
                <Link
                  to="/dashboard/accounts/$accountId"
                  params={{ accountId: row.account_id }}
                  search={{ range, tab: "trend", contract_type: contractTypeParam }}
                  aria-label={`Month-by-month trend for ${row.account_name ?? row.account_id}`}
                  title="Open this account's month-by-month trend"
                  className="-m-1 inline-block rounded p-1 hover:bg-muted"
                >
                  <Sparkline
                    values={row.trend ?? []}
                    label={`Margin trend for ${row.account_name ?? row.account_id}`}
                  />
                </Link>
              </td>
              <td className="px-4 py-2.5 text-right">
                <BelowTargetCell
                  gapDollars={row.gap_vs_benchmark}
                  marginPct={row.margin_pct}
                  benchmarkPct={data?.benchmark_pct ?? null}
                />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(row.revenue)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">{money(row.cost)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {row.internal_share_pct === null ? (
                  // A bare dash is the one cell on this row that says nothing at
                  // all. The share is a fraction of booked hours, so it is null
                  // exactly when no hours were booked -- not when the share is zero.
                  <span className="text-muted-foreground" title="No hours booked to this account">
                    —
                  </span>
                ) : (
                  `${row.internal_share_pct.toFixed(1)}%`
                )}
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums">
                {row.overdue_count > 0 ? (
                  <StatusChip
                    tone="bad"
                    title={`${row.overdue_count} overdue invoice${row.overdue_count === 1 ? "" : "s"}`}
                  >
                    {`${row.overdue_count} · ${money(row.overdue_value)}`}
                  </StatusChip>
                ) : (
                  <span className="text-muted-foreground" title="No overdue invoices">
                    —
                  </span>
                )}
              </td>
            </tr>
          ))}
        </DataTable>
      </Card>

      <CursorPagination
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={Boolean(data?.next_cursor)}
        hasPrev={Boolean(data?.prev_cursor)}
        onNext={() => grid.goNext(data?.next_cursor ?? null)}
        onPrev={() => grid.goPrev(data?.prev_cursor ?? null)}
        loading={isLoading}
        itemLabel="accounts"
      />
    </div>
  );
}
